import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostBinding,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { LoyaltyCurrencyService } from '@rd/core/services/loyalty-currency/loyalty-currency.service';
import { LoyaltyProgram, User } from '@rd/types';
import dayjs from 'dayjs';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TransferConnectV2SettingService } from '../../services/transfer-connect-v2-setting/transfer-connect-v2-setting.service';
import { TransferConnectV2MembershipDetailFormData } from '../../types';

interface LinkAccountDisclaimerParams {
  loyaltyCurrency: string;
  loyaltyProgramName: string;
}

@Component({
  selector: 'rd-tcv2-link-account-form',
  templateUrl: './link-account-form.component.html',
  styleUrls: ['./link-account-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TcV2LinkAccountFormComponent implements OnInit, OnDestroy {
  @HostBinding('class.rd-tcv2-link-account-form') componentClass = true;
  @Input() disablePrefilledFields: boolean;
  @Input() formData: TransferConnectV2MembershipDetailFormData;
  @Input() loading: boolean;
  @Input() loyaltyProgram: LoyaltyProgram;
  @Input() prefillForm: boolean;
  @Output()
  submitForm = new EventEmitter<TransferConnectV2MembershipDetailFormData>();
  @Input() user: User;

  checkoutForm: UntypedFormGroup;
  isSubmitted$: Subject<boolean> = new Subject();
  linkAccountDisclaimerParams: LinkAccountDisclaimerParams;
  shouldRenderLinkAccountFormDisclaimer: boolean;
  showDobField: boolean;

  private destroy$ = new Subject();

  constructor(
    private loyaltyCurrencyService: LoyaltyCurrencyService,
    private fb: UntypedFormBuilder,
    private cdRef: ChangeDetectorRef,
    private transferConnectV2SettingService: TransferConnectV2SettingService,
  ) {
    this.initForms();
  }

  ngOnInit(): void {
    this.shouldRenderLinkAccountFormDisclaimer =
      this.transferConnectV2SettingService.shouldRenderLinkAccountFormDisclaimer;
    this.showDobField =
      this.transferConnectV2SettingService.shouldShowDobFieldInLinkForm(
        this.loyaltyProgram.transferConnectIdentifier,
      );
    this.linkAccountDisclaimerParams = this.setLinkAccountDisclaimerParams();
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  private initForms(): void {
    this.checkoutForm = this.fb.group({
      membershipDetailForm: [''],
    });

    this.checkoutForm.statusChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((status) => {
        this.cdRef.markForCheck();
      });
  }

  buttonDisabled(): boolean {
    return this.loading;
  }

  onSubmit(): void {
    // Inform child forms that the parent form has been submitted, so they mark each of their components as touched
    this.isSubmitted$.next(true);

    if (this.checkoutForm.valid) {
      this.submitForm.emit(this.constructFormData());
    }
  }

  private constructFormData(): TransferConnectV2MembershipDetailFormData {
    let firstName;
    let lastName;

    if (
      this.transferConnectV2SettingService
        .shouldDisablePrefilledLinkAccountFields
    ) {
      firstName = this.user.firstName;
      lastName = this.user.lastName;
    } else {
      ({ firstName, lastName } =
        this.checkoutForm.controls.membershipDetailForm.value);
    }

    const { membershipNumber } =
      this.checkoutForm.controls.membershipDetailForm.value;

    const dateOfBirth = this.getDate(
      this.checkoutForm.controls.membershipDetailForm.value.dateOfBirth,
    );
    firstName = firstName.trim();
    lastName = lastName.trim();

    return {
      membershipNumber,
      firstName,
      lastName,
      dateOfBirth,
    };
  }

  setLinkAccountDisclaimerParams(): LinkAccountDisclaimerParams {
    return {
      loyaltyCurrency:
        // @ts-ignore
        this.loyaltyCurrencyService.loyaltyCurrencySelected[
          this.transferConnectV2SettingService
            .linkAccountDisclaimerLoyaltyProgramNameAttribute
        ],
      loyaltyProgramName: this.loyaltyProgram.name,
    };
  }

  private getDate(rawDate: Date | string): string | undefined {
    return this.showDobField
      ? dayjs.utc(rawDate).toISOString().split('T')[0]
      : undefined;
  }
}
